import {
  Component,
  inject,
  output,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule } from '@angular/common';

import {
  MessageService,
  ObserveIntersectingDirective,
  ProgressComponent
} from '@factor_ec/ui';
import { Operation } from '@factor_ec/utils';

import { Catalog } from '@/core/models/catalog';
import { AppService } from '@/core/app.service';
import { SessionService } from '@/core/session.service';
import { Beneficiary, BeneficiaryInput } from '@/main/models/connection';
import { BeneficiaryRepository } from '@/main/repositories/beneficiary-repository';

/**
 * @description Componente para crear o editar un beneficiario
 * @class BeneficiaryFormComponent
 */
@Component({
  selector: 'app-beneficiary-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormField,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    ProgressComponent,
    ObserveIntersectingDirective
  ],
  templateUrl: './beneficiary-form.component.html',
  styleUrl: './beneficiary-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BeneficiaryFormComponent {
  // Dependency injection
  public readonly appService = inject(AppService);
  private readonly beneficiaryRepository = inject(BeneficiaryRepository);
  private readonly dialogRef = inject(MatDialogRef<BeneficiaryFormComponent>);
  private readonly formBuilder = inject(FormBuilder);
  private readonly dialogData = inject<
    { beneficiary: Beneficiary } | undefined
  >(MAT_DIALOG_DATA, { optional: true });
  private readonly messageService = inject(MessageService);
  private readonly sessionService = inject(SessionService);

  // Events
  afterSubmit = output<Operation>();
  mutations = this.beneficiaryRepository.mutations();

  // Properties
  form: FormGroup = new FormGroup({});
  public status = signal<Catalog[]>([]);

  constructor() {
    this.initForm();
  }

  /**
   * @description Inicializa el formulario reactivo con valores por defecto o datos del diálogo
   * @private
   */
  private initForm(): void {
    this.status.set(
      this.sessionService.catalogs().filter(c => c.catalogId === 'ESTVI')
    );
    this.form = this.formBuilder.group({
      sequence: [null],
      id: [null],
      description: ['', [Validators.required, Validators.maxLength(200)]],
      email: [
        '',
        [Validators.required, Validators.email, Validators.maxLength(200)]
      ],
      statusId: [this.status()[1].id, [Validators.required, Validators.maxLength(50)]]
    });
    if (this.dialogData?.beneficiary) {
      this.form.patchValue(this.mapBeneficiary(this.dialogData.beneficiary));
    }
  }

  private mapBeneficiary(beneficiary: Beneficiary): BeneficiaryInput {
    return {
      sequence: beneficiary.sequence,
      id: beneficiary.id,
      description: beneficiary.description,
      email: beneficiary.email,
      statusId: (beneficiary.status?.id as 'PUB' | 'PRV') ?? 'PRV'
    };
  }

  /**
   * @description Envía el formulario y cierra el diálogo emitiendo el resultado
   * @returns {Promise<void>}
   */
  async submit(): Promise<void> {
    if (this.form.valid) {
      const lenderId = this.sessionService.settings()?.lenderId;
      const data = {
        lender: {
          documentType: 'N',
          lenderId
        },
        beneficiary: this.form.value
      };
      try {
        this.form.disable();
        const method = this.dialogData?.beneficiary?.sequence ? 'update' : 'add';
        let response;
        if (method === 'update') {
          response = await this.mutations.update(data);
          this.messageService.show(
            $localize`A new beneficiary has been updated successfully.`,
            { class: 'ft-message--success', icon: 'check--circle' }
          );
        } else {
          response = await this.mutations.create(data);
          this.messageService.show(
            $localize`A new beneficiary has been created successfully.`,
            { class: 'ft-message--success', icon: 'check--circle' }
          );
        }
        data.beneficiary.sequence = response?.data;
        this.afterSubmit.emit({ method, entity: data.beneficiary });
        this.dialogRef.close(data);
      } finally {
        this.form.enable();
      }
    }
  }
}
